import React from 'react';
import {
  BugAntIcon,
  ChartBarIcon,
  CircleStackIcon,
  CodeBracketIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { Badge, Spinner } from '../ui';

export interface WorkspaceChatArtifact {
  artifact_type?: string;
  artifact_id?: string;
  title?: string;
  render_kind?: string;
  source_type?: string;
  tool_name?: string;
  spec?: Record<string, any>;
  summary?: Record<string, any>;
  debug_refs?: Record<string, any>;
  metadata?: Record<string, any>;
}

interface RetrievedChunkLike {
  metadata?: Record<string, any>;
}

export function extractWorkspaceChatArtifactsFromMessage(
  metadata?: Record<string, any>,
  chunks?: RetrievedChunkLike[],
): WorkspaceChatArtifact[] {
  const candidates: WorkspaceChatArtifact[] = [];
  const append = (value: unknown) => {
    if (!Array.isArray(value)) return;
    value.forEach((item) => {
      if (item && typeof item === 'object') {
        candidates.push(item as WorkspaceChatArtifact);
      }
    });
  };

  append(metadata?.artifacts);
  chunks?.forEach((chunk) => append(chunk.metadata?.artifacts));

  const seen = new Set<string>();
  return candidates.filter((artifact) => {
    const key = String(artifact.artifact_id || `${artifact.artifact_type}-${seen.size}`);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function WorkspaceChatArtifactBlocks({
  artifacts,
}: {
  artifacts: WorkspaceChatArtifact[];
  onFollowUpClick?: (value: string) => void;
}) {
  const [debugOpen, setDebugOpen] = React.useState(false);
  const visibleArtifacts = artifacts.filter((artifact) => !isDebugDrawerArtifact(artifact));
  const debugArtifacts = artifacts.filter(isDebugDrawerArtifact);
  if (artifacts.length === 0) return null;

  return (
    <div className="min-w-0 max-w-full space-y-3">
      {visibleArtifacts.map((artifact, index) => (
        <WorkspaceChatArtifactBlock
          key={artifact.artifact_id || `${artifact.artifact_type}-${index}`}
          artifact={artifact}
        />
      ))}
      {debugArtifacts.length > 0 && (
        <div className="flex flex-col items-start gap-2">
          <button
            type="button"
            onClick={() => setDebugOpen((open) => !open)}
            title="Show message artifacts and debug details"
            aria-label="Show message artifacts and debug details"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition hover:border-gray-300 hover:bg-gray-50 hover:text-charcoal dark:border-dark-border dark:text-gray-400 dark:hover:border-gray-500 dark:hover:bg-dark-surface-2 dark:hover:text-gray-100"
          >
            <BugAntIcon className="h-4 w-4" />
          </button>
          {debugOpen && (
            <div className="w-full space-y-2 rounded-md border border-gray-200 bg-white p-2 dark:border-dark-border dark:bg-dark-surface">
              {debugArtifacts.map((artifact, index) => (
                <WorkspaceChatArtifactBlock
                  key={artifact.artifact_id || `debug-${artifact.artifact_type}-${index}`}
                  artifact={artifact}
                  compact
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WorkspaceChatArtifactBlock({
  artifact,
  compact = false,
}: {
  artifact: WorkspaceChatArtifact;
  compact?: boolean;
}) {
  const renderKind = artifact.render_kind || artifact.artifact_type || 'artifact';
  const title = artifact.title || artifactTitle(renderKind);
  const status = String(artifact.metadata?.status || artifact.spec?.status || '').toLowerCase();
  const bodyPadding = compact ? 'p-1.5' : renderKind === 'table' ? 'p-0' : 'p-2';

  return (
    <section className="min-w-0 max-w-full overflow-hidden rounded-md border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="flex min-h-7 flex-wrap items-center gap-1.5 border-b border-gray-100 px-2 py-1 dark:border-dark-border">
        <ArtifactIcon renderKind={renderKind} />
        <h4 className="min-w-0 flex-1 truncate text-xs font-semibold text-charcoal dark:text-gray-100">
          {title}
        </h4>
        {artifact.source_type && (
          <span className="rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0 text-[11px] font-medium leading-5 text-gray-600 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
            {artifact.source_type}
          </span>
        )}
        {status === 'loading' && <Spinner size="sm" />}
        {status === 'error' && <Badge variant="danger">Error</Badge>}
      </div>
      {renderKind === 'sql' && status !== 'loading' && status !== 'error' ? (
        <SqlArtifact artifact={artifact} />
      ) : (
        <div className={bodyPadding}>
          {status === 'loading' ? (
            <ArtifactState label="Loading artifact" />
          ) : status === 'error' ? (
            <ArtifactState label={String(artifact.metadata?.error || 'Artifact could not be rendered.')} tone="error" />
          ) : renderKind === 'table' ? (
            <TableArtifact artifact={artifact} />
          ) : renderKind === 'chart' ? (
            <ChartArtifact artifact={artifact} />
          ) : renderKind === 'debug_trace' ? (
            <DebugTraceArtifact artifact={artifact} />
          ) : (
            <ArtifactState label="Artifact preview unavailable" />
          )}
        </div>
      )}
    </section>
  );
}

function TableArtifact({ artifact }: { artifact: WorkspaceChatArtifact }) {
  const spec = artifact.spec || {};
  const columns = Array.isArray(spec.columns) ? spec.columns : [];
  const rows = Array.isArray(spec.rows) ? spec.rows : [];
  if (columns.length === 0 || rows.length === 0) {
    return <ArtifactState label="No rows returned" />;
  }

  return (
    <div className="max-w-full overflow-x-auto overscroll-x-contain">
      <table className="!my-0 w-max min-w-full border-collapse text-left">
        <thead>
          <tr className="bg-gray-50 dark:bg-dark-surface-2">
            {columns.map((column: any) => (
              <th
                key={column.key}
                className="border-b border-gray-200 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-gray-500 dark:border-dark-border dark:text-gray-400"
              >
                {column.label || column.key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-dark-surface">
          {rows.map((row: Record<string, any>, rowIndex: number) => (
            <tr key={rowIndex}>
              {columns.map((column: any) => (
                <td
                  key={column.key}
                  className="border-b border-gray-200 px-3 py-1.5 text-sm leading-5 text-charcoal dark:border-dark-border dark:text-gray-100"
                >
                  {formatCell(row[column.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {spec.truncated && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Showing a preview of {spec.row_count} rows.
        </p>
      )}
    </div>
  );
}

function ChartArtifact({ artifact }: { artifact: WorkspaceChatArtifact }) {
  const spec = artifact.spec || {};
  const data = Array.isArray(spec.data) ? spec.data : [];
  const xKey = String(spec.x_key || '');
  const yKey = Array.isArray(spec.y_keys) ? String(spec.y_keys[0] || '') : '';
  if (!xKey || !yKey || data.length === 0) {
    return <ArtifactState label="No chartable values returned" />;
  }
  const max = Math.max(...data.map((row: Record<string, any>) => Number(row[yKey]) || 0), 0);

  return (
    <div className="space-y-2" role="img" aria-label={artifact.title || 'Result chart'}>
      {data.map((row: Record<string, any>, index: number) => {
        const value = Number(row[yKey]) || 0;
        const width = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 4;
        return (
          <div key={index} className="grid grid-cols-[minmax(6rem,10rem)_1fr_auto] items-center gap-2 text-xs">
            <span className="truncate text-gray-600 dark:text-gray-300">{formatCell(row[xKey])}</span>
            <div className="h-2 rounded bg-gray-100 dark:bg-dark-surface-2">
              <div className="h-2 rounded bg-eliza-red" style={{ width: `${width}%` }} />
            </div>
            <span className="font-mono text-gray-600 dark:text-gray-300">{formatCell(value)}</span>
          </div>
        );
      })}
    </div>
  );
}

function SqlArtifact({ artifact }: { artifact: WorkspaceChatArtifact }) {
  const sql = String(artifact.spec?.sql || '').trim();
  if (!sql) return <ArtifactState label="No SQL available" />;
  return (
    <pre className="m-0 max-h-64 max-w-full overscroll-contain overflow-auto !bg-gray-50 px-2 py-2 font-mono text-code leading-5 !text-charcoal dark:!bg-dark-surface-2 dark:!text-gray-100">
      <span className="whitespace-pre !text-inherit">{sql}</span>
    </pre>
  );
}

function DebugTraceArtifact({ artifact }: { artifact: WorkspaceChatArtifact }) {
  const debugRef = (artifact.spec?.debug_ref || artifact.summary || {}) as Record<string, any>;
  const retrievalContext = (
    artifact.spec?.retrieval_context ||
    artifact.metadata?.retrieval_context ||
    {}
  ) as Record<string, any>;
  const selectedChunks = Array.isArray(retrievalContext.selected_chunks)
    ? retrievalContext.selected_chunks
    : [];

  return (
    <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
      <div className="flex flex-wrap gap-2">
        {debugRef.path && <Badge variant="secondary">{String(debugRef.path)}</Badge>}
        {debugRef.filename && !debugRef.path && (
          <Badge variant="secondary">{String(debugRef.filename)}</Badge>
        )}
        {debugRef.retrieval_backend && (
          <Badge variant="secondary">{String(debugRef.retrieval_backend)}</Badge>
        )}
      </div>
      {selectedChunks.length > 0 ? (
        <div className="space-y-1">
          {selectedChunks.map((chunk: Record<string, any>, index: number) => (
            <div
              key={`${chunk.fragment_id || chunk.canonical_name || index}`}
              className="rounded-md border border-gray-100 bg-gray-50 px-2 py-1 text-xs dark:border-dark-border dark:bg-dark-surface-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-mono text-charcoal dark:text-gray-100">
                  {chunk.canonical_name ||
                    chunk.fragment_id ||
                    chunk.document_type ||
                    'context chunk'}
                </span>
                {typeof chunk.score === 'number' && (
                  <span className="shrink-0 font-mono text-gray-500 dark:text-gray-400">
                    {chunk.score.toFixed(1)}
                  </span>
                )}
              </div>
              {chunk.document_type && (
                <div className="mt-0.5 text-gray-500 dark:text-gray-400">
                  {String(chunk.document_type)}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <ArtifactState label="Debug reference captured" />
      )}
    </div>
  );
}

function ArtifactState({ label, tone = 'muted' }: { label: string; tone?: 'muted' | 'error' }) {
  return (
    <div className={tone === 'error' ? 'text-sm text-red-600 dark:text-red-400' : 'text-sm text-gray-500 dark:text-gray-400'}>
      {label}
    </div>
  );
}

function ArtifactIcon({ renderKind }: { renderKind: string }) {
  const className = "h-3.5 w-3.5 text-gray-500 dark:text-gray-400";
  if (renderKind === 'table') return <TableCellsIcon className={className} />;
  if (renderKind === 'chart') return <ChartBarIcon className={className} />;
  if (renderKind === 'sql') return <CodeBracketIcon className={className} />;
  return <CircleStackIcon className={className} />;
}

function artifactTitle(renderKind: string) {
  if (renderKind === 'table') return 'Result table';
  if (renderKind === 'chart') return 'Result chart';
  if (renderKind === 'sql') return 'Generated SQL';
  if (renderKind === 'debug_trace') return 'Structured query debug';
  return 'Artifact';
}

function isDebugDrawerArtifact(artifact: WorkspaceChatArtifact): boolean {
  const renderKind = artifact.render_kind || artifact.artifact_type || 'artifact';
  return renderKind === 'chart' || renderKind === 'debug_trace';
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}
